import { useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { NON_DIGIT_REGEX } from "utils/regex";
import { OtpInputContainer } from "./OtpInput.styles";

/**
 * Segmented one-time-code field: one box per digit.
 *
 * `value` is a plain digit string ("1234"), so it stays left packed — box `i`
 * always renders `value[i]` and there is never a hole to strip before sending
 * the code to the API.
 */
export const OtpInput = ({
  value = "",
  onChange,
  length = 6,
  label = "",
  error = "",
  disabled = false,
  autoFocus = false,
}) => {
  const inputsRef = useRef([]);

  const focusInput = (index) => {
    const input = inputsRef.current[index];
    if (!input) return;
    input.focus();
    input.select();
  };

  const emit = (next) => {
    onChange(next.replace(NON_DIGIT_REGEX, "").slice(0, length));
  };

  const handleChange = (event, index) => {
    const raw = event.target.value;
    const typed = raw.replace(NON_DIGIT_REGEX, "");

    if (!typed) {
      // Deletions that arrive as a change rather than a keydown: the Delete
      // key, cut, and the soft keyboards that report no key. A non-digit
      // keystroke leaves the box alone instead of clearing it.
      if (raw === "" && value[index]) {
        const chars = value.split("");
        chars.splice(index, 1);
        emit(chars.join(""));
      }
      return;
    }

    // Autofill and fast typing can land several digits in a single box.
    if (typed.length > 1) {
      const next = (value.slice(0, index) + typed).slice(0, length);
      emit(next);
      focusInput(Math.min(next.length, length - 1));
      return;
    }

    const chars = value.split("");
    // Tapping a box past the filled range appends rather than leaving a gap.
    if (index < chars.length) chars[index] = typed;
    else chars.push(typed);

    emit(chars.join(""));
    if (index < length - 1) focusInput(index + 1);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const chars = value.split("");
      if (chars[index]) {
        // Delete in place; the digits behind it shift up, as in a text field.
        chars.splice(index, 1);
        emit(chars.join(""));
      } else if (index > 0) {
        chars.splice(index - 1, 1);
        emit(chars.join(""));
        focusInput(index - 1);
      }
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    }

    if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(NON_DIGIT_REGEX, "")
      .slice(0, length);
    if (!pasted) return;
    emit(pasted);
    focusInput(Math.min(pasted.length, length - 1));
  };

  return (
    <OtpInputContainer>
      {label ? <label className="otp-label">{label}</label> : null}
      <div className="otp-boxes">
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(element) => {
              inputsRef.current[index] = element;
            }}
            type="text"
            inputMode="numeric"
            // Lets the browser drop an SMS code straight into the field.
            autoComplete={index === 0 ? "one-time-code" : "off"}
            autoFocus={autoFocus && index === 0}
            maxLength={1}
            disabled={disabled}
            aria-label={`Digit ${index + 1}`}
            value={value[index] || ""}
            onChange={(event) => handleChange(event, index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onPaste={handlePaste}
            onFocus={(event) => event.target.select()}
            className={classNames("otp-box", { "is-invalid": Boolean(error) })}
          />
        ))}
      </div>
      {error ? <div className="form-error">{error}</div> : null}
    </OtpInputContainer>
  );
};

OtpInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  length: PropTypes.number,
  label: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
};
